import type { AxiosRequestConfig } from 'axios';
import { ApiError } from '../types';
import { ENDPOINTS } from '../endpoints';
import {
  MOCK_ACCESS_TOKEN,
  mockConversations,
  mockCrmEntries,
  mockCurrentUser,
  mockEvent,
  mockMatches,
  mockMessagesByMatch,
  mockTags,
} from './data';
import type { OnboardingPayload } from '@/entities/user';
import type { Match } from '@/entities/match';
import type { Message } from '@/entities/message';

const SIMULATED_LATENCY_MS = 300;

const delay = (ms = SIMULATED_LATENCY_MS) =>
  new Promise<void>((resolve) => window.setTimeout(resolve, ms));

let currentUser = { ...mockCurrentUser, onboardingCompleto: false };
let isLoggedIn = false;
const matches = [...mockMatches];
const conversations = [...mockConversations];
const crmEntries = [...mockCrmEntries];
const messagesByMatch: Record<string, Message[]> = { ...mockMessagesByMatch };

const matchPath = (config: AxiosRequestConfig, method: string, url: string): boolean =>
  (config.method ?? 'get').toLowerCase() === method.toLowerCase() && config.url === url;

const matchPrefix = (config: AxiosRequestConfig, method: string, prefix: string): boolean =>
  (config.method ?? 'get').toLowerCase() === method.toLowerCase() &&
  typeof config.url === 'string' &&
  config.url.startsWith(prefix);

const extractIdFromPath = (url: string, segmentIndex: number): string => {
  const parts = url.split('/').filter(Boolean);
  return parts[segmentIndex] ?? '';
};

const fail = (code: string, message: string, status = 400): never => {
  throw new ApiError({ code, message, status });
};

export const isMockable = (config: AxiosRequestConfig): boolean => {
  return typeof config.url === 'string';
};

export const handleMockRequest = async <T>(config: AxiosRequestConfig): Promise<T> => {
  await delay();
  const data = config.data as Record<string, unknown> | undefined;

  // ──────────────────────────────── AUTH ────────────────────────────────
  if (matchPath(config, 'POST', ENDPOINTS.AUTH.REGISTER)) {
    const email = (data?.email as string | undefined) ?? 'novo@knowhow.com';
    const nome = (data?.nome as string | undefined) ?? 'Novo Usuário';
    currentUser = {
      ...mockCurrentUser,
      id: `u-${Date.now()}`,
      email,
      nome,
      onboardingCompleto: false,
      plano: 'FREE',
    };
    isLoggedIn = true;
    return { accessToken: MOCK_ACCESS_TOKEN, user: currentUser } as T;
  }

  if (matchPath(config, 'POST', ENDPOINTS.AUTH.LOGIN)) {
    const email = (data?.email as string | undefined) ?? 'demo@knowhow.com';
    currentUser = { ...mockCurrentUser, email, onboardingCompleto: true };
    isLoggedIn = true;
    return { accessToken: MOCK_ACCESS_TOKEN, user: currentUser } as T;
  }

  if (matchPath(config, 'POST', ENDPOINTS.AUTH.LOGOUT)) {
    isLoggedIn = false;
    return undefined as T;
  }

  if (matchPath(config, 'POST', ENDPOINTS.AUTH.REFRESH)) {
    if (!isLoggedIn) fail('NO_SESSION', 'Sessão expirada', 401);
    return { accessToken: MOCK_ACCESS_TOKEN } as T;
  }

  if (matchPath(config, 'GET', ENDPOINTS.AUTH.ME)) {
    if (!isLoggedIn) fail('UNAUTHORIZED', 'Não autenticado', 401);
    return { user: currentUser } as T;
  }

  // ──────────────────────────────── USERS ───────────────────────────────
  if (matchPath(config, 'GET', ENDPOINTS.USERS.ME)) {
    if (!isLoggedIn) fail('UNAUTHORIZED', 'Não autenticado', 401);
    return { user: currentUser } as T;
  }

  if (matchPath(config, 'POST', ENDPOINTS.USERS.ONBOARDING)) {
    const payload = data as OnboardingPayload | undefined;
    currentUser = {
      ...currentUser,
      onboardingCompleto: true,
    };
    void payload;
    return { user: currentUser } as T;
  }

  if (matchPath(config, 'PATCH', ENDPOINTS.USERS.ME)) {
    currentUser = { ...currentUser, ...(data as Partial<typeof currentUser>) };
    return { user: currentUser } as T;
  }

  // ──────────────────────────────── TAGS ────────────────────────────────
  if (matchPath(config, 'GET', ENDPOINTS.TAGS.LIST)) {
    return { tags: mockTags } as T;
  }

  // ──────────────────────────────── EVENTS ──────────────────────────────
  if (matchPath(config, 'GET', ENDPOINTS.EVENTS.LIST)) {
    return { event: mockEvent } as T;
  }

  // ──────────────────────────────── MATCHES ─────────────────────────────
  if (matchPrefix(config, 'GET', '/matches/me/')) {
    return { matches } as T;
  }

  if (matchPrefix(config, 'PATCH', '/matches/') && config.url?.endsWith('/respond')) {
    const id = extractIdFromPath(config.url, 1);
    const idx = matches.findIndex((m) => m.id === id);
    if (idx < 0) fail('NOT_FOUND', 'Match não encontrado', 404);
    const target = matches[idx] as Match;
    const status = (data?.status as string | undefined) ?? 'accepted';
    const updated: Match = {
      ...target,
      status: status === 'accepted' ? 'CONVERSANDO' : 'PULADO',
    };
    matches[idx] = updated;
    return { match: updated } as T;
  }

  // ──────────────────────────────── CHAT ────────────────────────────────
  if (matchPath(config, 'GET', ENDPOINTS.CHAT.CONVERSATIONS)) {
    return { conversations } as T;
  }

  if (matchPrefix(config, 'GET', '/chat/messages/')) {
    const matchId = extractIdFromPath(config.url ?? '', 1);
    return { messages: messagesByMatch[matchId] ?? [] } as T;
  }

  if (matchPath(config, 'POST', ENDPOINTS.CHAT.MESSAGES_SEND)) {
    const matchId = (data?.matchId as string | undefined) ?? '';
    const content = (data?.content as string | undefined) ?? '';
    const type = ((data?.tipo as Message['type'] | undefined) ??
      (data?.type as Message['type'] | undefined) ??
      'TEXT') as Message['type'];
    const message: Message = {
      id: `msg-${Date.now()}`,
      matchId,
      authorId: currentUser.id,
      type,
      content,
      read: true,
      createdAt: new Date().toISOString(),
    };
    const existing = messagesByMatch[matchId] ?? [];
    messagesByMatch[matchId] = [...existing, message];
    return { message } as T;
  }

  if (matchPrefix(config, 'PATCH', '/chat/messages/') && config.url?.endsWith('/read')) {
    const matchId = extractIdFromPath(config.url, 1);
    const existing = messagesByMatch[matchId] ?? [];
    messagesByMatch[matchId] = existing.map((m) => ({ ...m, read: true }));
    return undefined as T;
  }

  // ──────────────────────────────── CRM ─────────────────────────────────
  if (matchPath(config, 'GET', ENDPOINTS.CRM.LIST_ME)) {
    return { entries: crmEntries } as T;
  }

  if (matchPrefix(config, 'PATCH', '/crm/') && config.url?.endsWith('/stage')) {
    const id = extractIdFromPath(config.url, 1);
    const idx = crmEntries.findIndex((e) => e.id === id);
    if (idx < 0) fail('NOT_FOUND', 'Registro não encontrado', 404);
    const updated = {
      ...(crmEntries[idx] as (typeof crmEntries)[number]),
      ...(data as Partial<(typeof crmEntries)[number]>),
      updatedAt: new Date().toISOString(),
    };
    crmEntries[idx] = updated;
    return { entry: updated } as T;
  }

  // ─────────────────────────────── DEFAULT ──────────────────────────────
  throw new ApiError({
    code: 'MOCK_NOT_IMPLEMENTED',
    message: `Mock não implementado para ${config.method?.toUpperCase()} ${config.url}`,
    status: 501,
  });
};
